/**
 * One thread, as Chats (F6) and the side panel (F9) both show it: the opening
 * message in a band (on a phone, the first row, scrolling with the rest), the replies following new blocks with
 * `use-stick-to-bottom`, each unfinished turn's line (spec §7.3), and the
 * reply composer. Both surfaces render this, so they share the projection and
 * the `['thread', id]` query.
 */
import type { Uploaded } from "../../app/api";
import { editedBody, filesProps, useComposerFiles, wordlessBody } from "../files/composer-files";
import { Button, buttonVariants } from "@crew/ui/components/button";
import { Composer, type ComposerHandle } from "@crew/ui/components/composer";
import { AgentAvatar, PersonAvatar } from "@crew/ui/components/crew-avatar";
import { Link } from "@crew/ui/components/link";
import { Spinner } from "@crew/ui/components/spinner";
import { StatusDot, StatusLabel } from "@crew/ui/components/status";
import { TasksIcon } from "@crew/ui/components/nav-icons";
import { ThreadMessage, WakingIndicator } from "@crew/ui/components/thread";
import { Eyebrow } from "@crew/ui/components/typography";
import { cn } from "@crew/ui/lib/utils";
import {
  ArrowDown,
  Check,
  ChevronDown,
  CircleAlert,
  FileText,
  Globe,
  ListOrdered,
  Pencil,
  Smartphone,
  X,
} from "lucide-react";
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
  type RefObject,
} from "react";
import {
  StickToBottom,
  useStickToBottomContext,
  type StickToBottomContext,
} from "use-stick-to-bottom";
import { ApiError, errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { useWide } from "../../app/wide";
import { useAskGate } from "../setup/api";
import { AskNotes } from "../setup/ask-gate";
import { gateNotice } from "../setup/ask-notice";
import { canAsk } from "../setup/onboarding";
import { useHolder, type Handoff } from "../computer/api";
import { DesktopSheet } from "../computer/desktop-sheet";
import { WorkLine } from "@crew/ui/components/work-fold";
import { TurnWork } from "./work-fold";
import { LiveBrowser, MovedOnBrowser } from "./browser-frames";
import { TurnMade } from "./made";
import { ThreadRoutines } from "./thread-routines";
import { useWorkingLabel } from "./work";
import { HandoffCard } from "../computer/handoff-card";
import { ApprovalCard } from "../approvals/approval-card";
import { ChoiceCard } from "./choice-card";
import { AppChatCard } from "../apps/app-card";
import type { Approval } from "../approvals/api";
import {
  useEarlierMessages,
  usePostMessage,
  useRetryTurn,
  useStopTurn,
  useTakeBack,
  type Ask,
  type ThreadDetail,
  type Turn,
} from "./api";
import { authorOf } from "./authors";
import { messageTime, replyCount, replyCountLabel, shortTime } from "./format";
import { useThreadProjection } from "./live";
import { outbox, unsentLayout, useUnsent, type UnsentView } from "./outbox";
import type { DocItem } from "../docs/api";
import { ASK_TEXT, useAttach } from "../docs/attach";
import { ClampedText } from "./clamped-text";
import { lineDoc } from "../docs/doc-view";
import { AttachedDocChip, ComposerDocChip, DocUpdatedLine, ThreadDocCard } from "../docs/parts";
import type { TaskItem } from "../tasks/api";
import { ThreadTaskCard } from "../tasks/parts";
import { lineTask } from "../tasks/task-view";
import { lineArea } from "../areas/area-view";
import { RoutineLine } from "../routines/parts";
import { lineRoutine } from "../routines/routine-view";
import { attachmentsOf, isPlaceholder } from "../messaging/attached";
import { Attachments } from "../messaging/attachments";
import { MessagingFailedLine, ViaNote } from "../messaging/parts";
import {
  emptyProjection,
  liveStateOf,
  projectedMessages,
  type Projection,
  type ViewMessage,
} from "./projection";
import { ReplyMarkdown } from "./reply-markdown";
import {
  liveLine,
  fallbackNote,
  isFailureLine,
  memoryNoteFor,
  briefNotesFor,
  textedNoteFor,
  offersReconnect,
  continuesRow,
  olderThan,
  retryableTurn,
  threadLayout,
  toolsLinesFor,
  type ComposerQueueItem,
  type Row,
  type ToolsLine,
  type TurnLine,
} from "./turn-state";

type Size = "md" | "sm";

/** The two densities: Chats (`md`) and the side panel (`sm`). */
const DENSITY = {
  md: {
    avatar: 28,
    text: "text-body",
    markdown: "",
    band: "border-b border-border px-7 pt-[22px] pb-[18px]",
    clamp: "max-h-[6lh]",
    continued: "-mt-3",
    rows: "gap-5 px-7 pt-[18px] pb-12",
    composer: "px-7 pb-5",
    note: "px-7 py-6",
    highlight: false,
  },
  sm: {
    avatar: 24,
    text: "text-[14px] leading-[21px]",
    markdown: "space-y-3",
    band: "",
    clamp: "max-h-[4lh]",
    continued: "-mt-2",
    rows: "gap-4 px-5 pt-4 pb-12",
    composer: "border-t border-border px-4 pt-3 pb-4",
    note: "px-5 py-4",
    highlight: true,
  },
} satisfies Record<Size, Record<string, string | number | boolean>>;

function Avatar({ message, me, size }: { message: ViewMessage; me: Me; size: Size }) {
  const face = authorOf(message, me);
  const px = DENSITY[size].avatar;
  if (face.kind === "agent") return <AgentAvatar glyph={face.glyph} tint={face.tint} size={px} />;
  return <PersonAvatar initials={face.initials} tint={face.tint} size={px} />;
}

/** A `doc_card` (slice 2d spec §8): live from the thread's `docs`, else by its id. */
function DocCardBody({
  message,
  docs,
  size,
  me,
}: {
  message: ViewMessage;
  docs: Map<string, DocItem>;
  size: Size;
  me: Me;
}) {
  const docId = String(message.data.doc_id);
  return (
    <div className="mt-1 flex">
      <ThreadDocCard
        listed={docs.get(docId)}
        docId={docId}
        fallbackTitle={message.body}
        size={size}
        me={me}
      />
    </div>
  );
}

/**
 * A person's text, plain with its line breaks; an attached doc as its chip, never the claim (§11.4).
 * `clamp` holds the pinned opener to a few lines, with Show more.
 */
function PersonText({
  message,
  docs,
  className,
  clamp,
  from,
}: {
  message: ViewMessage;
  docs: Map<string, DocItem>;
  className: string;
  clamp?: string;
  /** Who sent it, for its photos' alt text. */
  from: string;
}) {
  const docId = typeof message.data.doc_id === "string" ? message.data.doc_id : null;
  // A text of photos or files alone: they show, not "📷 Photo" (slice 2f spec §10).
  const wordless = isPlaceholder(message.body, attachmentsOf(message.data));
  return (
    <>
      {wordless ? null : clamp ? (
        <ClampedText text={message.body} clamp={clamp} className={className} />
      ) : (
        <p className={cn("whitespace-pre-wrap text-foreground", className)}>{message.body}</p>
      )}
      {/* Save to Docs (slice 5b) once the message is in the thread, not while it's being sent. */}
      <Attachments
        data={message.data}
        from={from}
        threadId={(message as { unsent?: string }).unsent ? undefined : message.thread_id}
      />
      {docId ? <AttachedDocChip docId={docId} title={docs.get(docId)?.title} /> : null}
    </>
  );
}

/** The thread's handoffs by id (slice 6c §7.1), for its handoff cards. */
const ThreadHandoffs = createContext<Map<string, Handoff>>(new Map());

/**
 * A `handoff_card` (slice 6c §7.1): live from the thread's `handoffs`, else
 * its reason. On a phone it takes the column's whole width, under the
 * avatar too (board 90b).
 */
function HandoffCardBody({ message, me, size }: { message: ViewMessage; me: Me; size: Size }) {
  const handoffs = useContext(ThreadHandoffs);
  return (
    <div className={cn("mt-1 flex", size === "md" && "max-md:-ml-10 max-md:w-[calc(100%+2.5rem)]")}>
      <HandoffCard
        handoff={handoffs.get(String(message.data.handoff_id))}
        fallbackReason={message.body}
        me={me}
      />
    </div>
  );
}

const handoffsById = (detail: ThreadDetail) =>
  new Map((detail.handoffs ?? []).map((h) => [h.id, h]));

/** The thread's approvals by id (slice 3 §8), for its approval cards. */
const ThreadApprovals = createContext<Map<string, Approval>>(new Map());

/** An `approval_card` (slice 3 §8): live from the thread's `approvals`, the whole column on a phone. */
function ApprovalCardBody({ message, me, size }: { message: ViewMessage; me: Me; size: Size }) {
  const approvals = useContext(ThreadApprovals);
  return (
    <div className={cn("mt-1 flex", size === "md" && "max-md:-ml-10 max-md:w-[calc(100%+2.5rem)]")}>
      <ApprovalCard
        approval={approvals.get(String(message.data.approval_id))}
        fallbackTitle={message.body}
        me={me}
      />
    </div>
  );
}

/** S-525: the agent's question, from its own message's data, in the reply's column at both widths. */
function ChoiceCardBody({ message, me }: { message: ViewMessage; me: Me }) {
  return (
    <div className="mt-1 flex">
      <ChoiceCard messageId={message.id} threadId={message.thread_id} data={message.data} me={me} />
    </div>
  );
}

/**
 * The cards that render live: a handoff's (6c) and an approval's (slice 3)
 * from the thread's rows, a question's (S-525) from its message.
 */
const LIVE_CARDS: Partial<
  Record<ViewMessage["kind"], (p: { message: ViewMessage; me: Me; size: Size }) => ReactNode>
> = {
  handoff_card: (p) => <HandoffCardBody {...p} />,
  approval_card: (p) => <ApprovalCardBody {...p} />,
  choice_card: (p) => <ChoiceCardBody {...p} />,
  // Slice 6i: the app the agent published, with Open.
  app_card: ({ message, size }) => (
    <div className="mt-1 flex">
      <AppChatCard
        appId={String(message.data.app_id)}
        fallbackTitle={message.body}
        version={Number(message.data.version) || 1}
        size={size}
      />
    </div>
  ),
};

/** A person's text is plain, with its line breaks; an agent's is Markdown, streamed. */
function Body({
  message,
  size,
  streaming,
  tasks,
  docs,
  me,
  clamp,
}: {
  message: ViewMessage;
  size: Size;
  streaming: boolean;
  tasks: Map<string, TaskItem>;
  /** The thread's docs by id, for its doc cards and chips (slice 2d spec §8). */
  docs: Map<string, DocItem>;
  me: Me;
  clamp: string | undefined;
}) {
  const d = DENSITY[size];
  if (message.kind === "task_card") {
    const person = message.author_agent_id ? null : authorOf(message, me).name;
    return (
      <div className="mt-1 flex">
        <ThreadTaskCard
          task={tasks.get(String(message.data.task_id))}
          fallbackTitle={person ? message.body.split("\n")[0] : message.body}
          askedBy={person ? (person.split(/\s+/)[0] ?? person) : null}
          size={size}
        />
      </div>
    );
  }
  if (message.kind === "doc_card")
    return <DocCardBody message={message} docs={docs} size={size} me={me} />;
  const live = LIVE_CARDS[message.kind];
  if (live) return live({ message, me, size });
  if (!message.author_agent_id) {
    return (
      <PersonText
        message={message}
        docs={docs}
        className={d.text}
        clamp={clamp}
        from={authorOf(message, me).name}
      />
    );
  }
  return (
    <ReplyMarkdown
      className={cn("text-foreground", d.text, d.markdown)}
      streaming={streaming}
      titles={{ docs, tasks }}
    >
      {message.body}
    </ReplyMarkdown>
  );
}

function MessageView({
  message,
  turn,
  me,
  size,
  tasks,
  docs,
  clamp,
  highlight = false,
  note = null,
  memory = [],
  texted = null,
  tools = [],
  continued = false,
  sends,
}: {
  message: ViewMessage;
  turn: Turn | undefined;
  me: Me;
  size: Size;
  /** The thread's tasks by id, for its cards. */
  tasks: Map<string, TaskItem>;
  /** The thread's docs by id, for its doc cards and chips (slice 2d spec §8). */
  docs: Map<string, DocItem>;
  /** The pinned opening message's clamp: a person's text is held to it, with Show more. */
  clamp?: string;
  highlight?: boolean;
  /** A quiet line under the reply: "Answered by …, a fallback" (slice 1c spec §6.3). */
  note?: string | null;
  /**
   * "Updated its notes", linking to the file (slice 1d spec §6.2), and
   * "Updated the Garage gym brief", to its editor (slice 6d spec §8.5).
   */
  memory?: { text: string; href: string }[];
  /** "Texted to you" under a routine run's reply (slice 5 spec §11.4). */
  texted?: string | null;
  /** The turn's web lookups, and the copper "used up" line (slice 2a spec §5). */
  tools?: ToolsLine[];
  /** Follows on from the same agent's message in the same turn: no avatar or header. */
  continued?: boolean;
  /** Retry and Edit for a reply this tab couldn't send. */
  sends?: Sends;
}) {
  const unsent = (message as UnsentView).unsent;
  return (
    <div
      data-author={message.author_agent_id ? "agent" : "person"}
      data-message-id={message.id}
      data-turn-id={message.turn_id ?? undefined}
      data-unsent={unsent}
      className={unsent === "sending" ? "opacity-70" : undefined}
    >
      <ThreadMessage
        size={size}
        highlight={highlight}
        continued={continued}
        className={continued ? DENSITY[size].continued : undefined}
        avatar={<Avatar message={message} me={me} size={size} />}
        author={authorOf(message, me).name}
        time={messageTime(message, me.workspace.timezone)}
      >
        <Body
          message={message}
          size={size}
          streaming={turn?.status === "running"}
          tasks={tasks}
          docs={docs}
          me={me}
          clamp={clamp}
        />
        {message.author_agent_id ? null : <ViaNote data={message.data} />}
        <ToolsNote lines={tools} />
        <FallbackNote note={note} />
        {memory?.map((m) => (
          <MemoryNote key={m.href} memory={m} />
        ))}
        <TextedNote text={texted} />
        <NotSent message={message} sends={sends} />
      </ThreadMessage>
    </div>
  );
}

/** What a thread's rows can do with a reply this tab couldn't send (`outbox.ts`). */
type Sends = { retry: (id: string) => void; edit: (id: string) => void };

/**
 * "Not sent", why, and Retry (the same id again: never sent twice) or Edit
 * (its words back in the composer).
 */
function NotSent({ message, sends }: { message: UnsentView; sends?: Sends }) {
  if (message.unsent !== "failed" || !sends) return null;
  const error = outbox.find(message.thread_id, message.id)?.error;
  return (
    <div
      data-not-sent
      className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-meta text-muted-foreground"
    >
      <span role="alert" className="inline-flex items-center gap-1.5 text-brick">
        <StatusDot tone="error" />
        Not sent
      </span>
      {error ? <span>{errorHint(error)}</span> : null}
      <Button size="xs" variant="outline" onClick={() => sends.retry(message.id)}>
        Retry
      </Button>
      <Button size="xs" variant="ghost" onClick={() => sends.edit(message.id)}>
        Edit
      </Button>
    </div>
  );
}

/**
 * "Searched the web once · read 2 pages" under the reply, with a globe; the
 * copper "Web lookups used up for today" line when the day's cap refused one
 * (slice 2a spec §5).
 */
export function ToolsNote({ lines }: { lines: ToolsLine[] }) {
  return lines.map((line) => {
    const Icon = line.kind === "capped" ? CircleAlert : Globe;
    return (
      <p
        key={line.kind}
        data-tools-note={line.kind}
        className={cn(
          "mt-1.5 flex items-center gap-1.5 text-xs/4",
          line.kind === "capped" ? "text-copper" : "text-muted-foreground",
        )}
      >
        <Icon aria-hidden className="size-3 shrink-0" />
        {line.text}
      </p>
    );
  });
}

/** "Answered by GPT-5.5, a fallback", quietly under the reply (slice 1c spec §5.4, §6.3). */
function FallbackNote({ note }: { note: string | null }) {
  if (!note) return null;
  return (
    <p data-fallback-note className="mt-1.5 text-meta text-muted-foreground">
      {note}
    </p>
  );
}

/** "Updated its notes", quietly under the reply, to the file on the Memory page (slice 1d spec §6.2). */
function MemoryNote({ memory }: { memory: { text: string; href: string } }) {
  return (
    <p data-memory-note className="mt-1.5 text-meta text-muted-foreground">
      <Link href={memory.href} className="underline-offset-2 hover:text-foreground hover:underline">
        {memory.text}
      </Link>
    </p>
  );
}

/**
 * "Texted to you", in moss with a phone, under a routine run's reply (slice 5
 * spec §11.4); "Emailed to you" with a check, under an email chat's (slice 2h §8.3).
 */
function TextedNote({ text }: { text: string | null }) {
  if (!text) return null;
  const Icon = text.startsWith("Emailed") ? Check : Smartphone;
  return (
    <p data-texted-note className="mt-1.5 flex items-center gap-1.5 text-meta text-moss">
      <Icon aria-hidden className="size-3.5 shrink-0" strokeWidth={2} />
      {text}
    </p>
  );
}

function RetryButton({ turn, threadId }: { turn: Turn; threadId: string }) {
  const retry = useRetryTurn(threadId);
  return (
    <>
      <Button
        size="xs"
        variant="outline"
        disabled={retry.isPending}
        onClick={() => retry.mutate(turn.id)}
      >
        Retry
      </Button>
      {retry.error ? <span className="text-brick">{errorHint(retry.error)}</span> : null}
    </>
  );
}

/**
 * Reconnect on the `ai_auth` line (slice 1b spec §5, §6.4), to Settings → AI,
 * at the default model's card (slice 1c spec §6.3), until the AI is
 * connected again; then the turn's Retry takes over.
 */
function ReconnectButton() {
  return (
    <Link
      href="/settings#ai-default"
      className={buttonVariants({ size: "xs", variant: "outline" })}
    >
      Reconnect
    </Link>
  );
}

/**
 * "No reply.", a failure with Retry while `can_retry`, the recovery notice
 * (§3.2), or "Your AI connection stopped working." with Reconnect.
 */
function SystemLine({
  message,
  turns,
  threadId,
  me,
}: {
  message: ViewMessage;
  turns: Map<string, Turn>;
  threadId: string;
  me: Me;
}) {
  // Retry once asks go through again (slice 1j §4.3): a lost update's line waits for it.
  const retryable = canAsk(me.computer) ? retryableTurn(message, turns) : null;
  // Settings → AI is the owner's (slice 6a §6.5).
  const ai = me.computer?.ai;
  const reconnect =
    me.user.role === "owner" && offersReconnect(message, ai?.status, ai?.updating === true);
  return (
    <div
      data-author="system"
      data-event={String(message.data.event)}
      className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-meta text-muted-foreground"
    >
      {/* The dot stays beside its words when a long line wraps (a lost update's, slice 1j). */}
      <span className="flex min-w-0 items-start gap-x-3">
        {isFailureLine(message) ? <StatusDot tone="error" className="mt-1.5 shrink-0" /> : null}
        <LineBody message={message} />
      </span>
      {reconnect ? <ReconnectButton /> : null}
      {retryable ? <RetryButton turn={retryable} threadId={threadId} /> : null}
    </div>
  );
}

/**
 * A task line links to its task (slice 2b spec §5), "Updated <title>" to its
 * doc (slice 2d spec §8), a routine line to its routine (slice 5 spec
 * §11.4); the rest are plain.
 */
function LineBody({ message }: { message: ViewMessage }) {
  const docId = lineDoc(message);
  if (docId) return <DocUpdatedLine docId={docId} text={message.body} />;
  // Slice 5 (spec §11.4): "Made a reminder: …" and a reminder's "Reminder · 6:30 pm".
  const routineId = lineRoutine(message);
  if (routineId) return <RoutineLine routineId={routineId} text={message.body} />;
  // Slice 6d (spec §7.2): "Made the area …", and Move chats in on its page.
  const areaSlug = lineArea(message);
  if (areaSlug) {
    return (
      <span>
        {message.body} ·{" "}
        <Link
          href={`/areas/${areaSlug}?move=1`}
          data-area-line={areaSlug}
          className="underline underline-offset-2 hover:text-foreground"
        >
          Move chats and docs in
        </Link>
      </span>
    );
  }
  const taskId = lineTask(message);
  if (!taskId) return <span>{message.body}</span>;
  return (
    <Link
      href={`/tasks/${taskId}`}
      data-task-line={taskId}
      className="inline-flex items-center gap-1.5 underline-offset-2 hover:text-foreground hover:underline"
    >
      <TasksIcon size={12} className="shrink-0" />
      {message.body}
    </Link>
  );
}

/**
 * Opens the Watch sheet for a turn (slice 6c §4). Null where the computer's
 * desktop can't be shown (E2B, the local computer, one in `error`).
 */
const WatchTurn = createContext<((turnId: string) => void) | null>(null);

/**
 * Watch for a thread's turns (slice 6c §4) and its sheet. The sheet follows
 * the turn it opened on, and stays when that turn ends, saying Done.
 */
function Watching({
  turns,
  me,
  children,
}: {
  turns: Map<string, Turn>;
  me: Me;
  children: ReactNode;
}) {
  const [watching, setWatching] = useState<string | null>(null);
  const turn = watching ? turns.get(watching) : undefined;
  return (
    <WatchTurn.Provider value={me.computer?.desktop ? setWatching : null}>
      <DesktopSheet
        open={watching !== null}
        onClose={() => setWatching(null)}
        agentName={me.agents.find((a) => a.id === turn?.agent_id)?.name ?? "Chief of Staff"}
        line={turn?.progress?.text ?? null}
        done={turn?.status !== "running"}
      />
      {children}
    </WatchTurn.Provider>
  );
}

const WatchEye = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden className="shrink-0">
    <path
      d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    />
    <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
  </svg>
);

/** Board 92: Watch, beside the browser's line (slice 6c §4). */
function WatchButton({ turnId }: { turnId: string }) {
  const watch = useContext(WatchTurn);
  if (!watch) return null;
  return (
    <button
      type="button"
      data-watch
      onClick={() => watch(turnId)}
      className="pressable inline-flex h-[26px] items-center gap-1.5 rounded-[7px] bg-card px-2.5 text-meta leading-4 font-medium text-foreground shadow-[inset_0_0_0_1px_var(--color-border)] hover:bg-accent max-md:h-11 max-md:px-3.5"
    >
      <WatchEye />
      Watch
    </button>
  );
}

/** Slice 1q: the projection, for a running turn's Working state from its live events. */
const ThreadLive = createContext<Projection>(emptyProjection);

/**
 * S-509: a running turn's line, "Working for 12s", ticking, with what it's
 * doing now under it (its live state, slice 1q, else the progress line), and
 * Watch beside it on the computer. Slice 6f: the browser's newest picture
 * under the line while it's on the computer, folded into a row above the line
 * once it moves on.
 */
function LiveLine({ turn, line }: { turn: Turn; line: Extract<TurnLine, { kind: "working" }> }) {
  const label = useWorkingLabel(turn.started_at);
  const live = liveLine(liveStateOf(useContext(ThreadLive), turn.id));
  const frame = line.frame;
  return (
    <WorkLine
      tone="live"
      summary={label}
      className="self-start pl-10"
      current={live ?? line.progress}
      above={frame && !line.onComputer ? <MovedOnBrowser turn={turn} frame={frame} /> : null}
      below={frame && line.onComputer ? <LiveBrowser turn={turn} frame={frame} /> : null}
      trailing={line.onComputer ? <WatchButton turnId={turn.id} /> : null}
    />
  );
}

function TurnLineView({ line, turn }: { line: TurnLine; turn: Turn }) {
  if (line.kind === "working") return <LiveLine turn={turn} line={line} />;
  if (line.kind === "waking") {
    return <WakingIndicator label={line.text} className="self-start" />;
  }
  return (
    <span role="status">
      <StatusLabel tone="queued">{line.text}</StatusLabel>
    </span>
  );
}

/** S-509: a finished turn's fold, with who worked and when for the phone's sheet. */
function WorkRowView({
  row,
  me,
  threadId,
  tasks,
}: {
  row: Extract<Row, { kind: "work" }>;
  me: Me;
  threadId: string;
  tasks: Map<string, TaskItem>;
}) {
  const agent = me.agents.find((a) => a.id === row.turn.agent_id);
  const at = row.turn.finished_at;
  return (
    <TurnWork
      turn={row.turn}
      threadId={threadId}
      quiet={row.quiet}
      agentName={agent?.name ?? "Chief of Staff"}
      time={at ? shortTime(at, me.workspace.timezone) : ""}
      task={row.task ? tasks.get(row.task) : undefined}
      viewerId={me.user.id}
    />
  );
}

/**
 * S-509 M3: the rows there when this thread first showed. Any row not among
 * them arrived since, and rises in; switching threads takes a new snapshot.
 */
function useShownRows(threadId: string, rows: Row[]): (key: string) => string {
  const snapshot = () => ({ thread: threadId, keys: new Set(rows.map((r) => r.key)) });
  const [shown, setShown] = useState(snapshot);
  if (shown.thread !== threadId) setShown(snapshot());
  // A row that was there sits in the list as it is; a new one gets `row-enter`.
  return (key) => (shown.keys.has(key) ? "contents" : "row-enter");
}

/** A system message's row: a failed text or email, nothing for an approval's close, else its line. */
function SystemRow({
  message,
  turns,
  threadId,
  me,
  size,
}: {
  message: ViewMessage;
  turns: Map<string, Turn>;
  threadId: string;
  me: Me;
  size: Size;
}) {
  // Slice 2e (spec §5.3): a reply the outbox gave up texting.
  if (message.data.event === "messaging_failed") return <MessagingFailedLine size={size} />;
  if (message.data.event === "email_failed") return <MessagingFailedLine size={size} email />;
  // Slice 3 §5.2: an approval's close is its card's own line; this one only starts the turn.
  if (message.data.event === "approval") return null;
  return <SystemLine message={message} turns={turns} threadId={threadId} me={me} />;
}

/** The thread's rows, each a new one rising in (S-509 M3). */
function ThreadRowList({
  rows,
  ...shared
}: { rows: Row[] } & Omit<Parameters<typeof RowView>[0], "row" | "continued">) {
  const enter = useShownRows(shared.threadId, rows);
  return rows.map((row, i) => (
    <div key={row.key} className={enter(row.key)}>
      <RowView row={row} continued={continuesRow(rows[i - 1], row)} {...shared} />
    </div>
  ));
}

function RowView({
  row,
  continued = false,
  turns,
  tasks,
  docs,
  me,
  size,
  threadId,
  sends,
}: {
  row: Row;
  continued?: boolean;
  sends?: Sends;
  turns: Map<string, Turn>;
  tasks: Map<string, TaskItem>;
  /** The thread's docs by id, for its doc cards and chips (slice 2d spec §8). */
  docs: Map<string, DocItem>;
  me: Me;
  size: Size;
  threadId: string;
}) {
  if (row.kind === "work") {
    return <WorkRowView row={row} me={me} threadId={threadId} tasks={tasks} />;
  }
  if (row.kind === "made") return <TurnMade row={row} docs={docs} me={me} />;
  if (row.kind === "turn") {
    return (
      <div data-turn-line={row.line.kind} data-turn-id={row.turn.id}>
        <TurnLineView line={row.line} turn={row.turn} />
      </div>
    );
  }
  const m = row.message;
  if (m.kind === "system") {
    return <SystemRow message={m} turns={turns} threadId={threadId} me={me} size={size} />;
  }
  const turn = turns.get(m.turn_id ?? "");
  const note = fallbackNote(m, turns);
  const notes = memoryNoteFor(m, turns, me.agents);
  const memory = [...(notes ? [notes] : []), ...briefNotesFor(m, turns, me.areas)];
  const texted = textedNoteFor(m, turns, me.user.id);
  const tools = toolsLinesFor(m, turns);
  return (
    <MessageView
      message={m}
      turn={turn}
      me={me}
      size={size}
      tasks={tasks}
      docs={docs}
      continued={continued}
      note={note}
      memory={memory}
      texted={texted}
      tools={tools}
      sends={sends}
    />
  );
}

const REDUCED = "(prefers-reduced-motion: reduce)";

/** Whether motion should be kept to nothing (S-509 M6). */
function useReducedMotion(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const q = window.matchMedia(REDUCED);
      q.addEventListener("change", onChange);
      return () => q.removeEventListener("change", onChange);
    },
    () => window.matchMedia(REDUCED).matches,
    () => false,
  );
}

/** How the thread follows the bottom as it grows: gliding, or at once with reduced motion. */
function useFollowResize(): "instant" | "smooth" {
  return useReducedMotion() ? "instant" : "smooth";
}

function ScrollToBottom() {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();
  if (isAtBottom) return null;
  return (
    <Button
      variant="secondary"
      size="icon-sm"
      aria-label="Scroll to the latest"
      onClick={() => void scrollToBottom()}
      className="absolute bottom-3 left-1/2 -translate-x-1/2 border-border shadow-ring"
    >
      <ArrowDown />
    </Button>
  );
}

/**
 * The follow-ups waiting behind a reply, in a tray tucked behind the top of
 * the reply box: "Queued", how many, and a line each. Edit takes the words
 * back into the box and × drops them, until the computer has the turn. Steer
 * now isn't here: OpenClaw can't hand a message to a reply it already started.
 */
function ComposerQueue({
  queue,
  me,
  sends,
  onEdit,
}: {
  queue: ComposerQueueItem[];
  me: Me;
  sends: Sends;
  onEdit: OnEdit;
}) {
  const threadId = queue[0]?.message.thread_id ?? "";
  const takeBack = useTakeBack(threadId);
  const [open, setOpen] = useState(true);
  const listId = useId();
  if (queue.length === 0) return null;
  return (
    <div
      data-composer-queue
      className="mx-3 -mb-3 rounded-t-tile bg-secondary/70 px-2 pt-1 pb-4 shadow-composer"
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((o) => !o)}
        className="flex h-7 w-full items-center gap-2 rounded-md px-1.5 text-meta text-muted-foreground hover:text-foreground"
      >
        <ListOrdered aria-hidden className="size-3.5" />
        <span>Queued</span>
        <span className="ml-auto tabular-nums">{queue.length}</span>
        <ChevronDown
          aria-hidden
          className={cn("size-3.5 transition-transform", !open && "-rotate-90")}
        />
      </button>
      {open ? (
        <ul id={listId}>
          {queue.map((item) => (
            <QueueItem
              key={item.message.key}
              item={item}
              me={me}
              sends={sends}
              onEdit={onEdit}
              takeBack={takeBack}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function QueueItem({
  item,
  me,
  sends,
  onEdit,
  takeBack,
}: {
  item: ComposerQueueItem;
  me: Me;
  sends: Sends;
  onEdit: OnEdit;
  takeBack: ReturnType<typeof useTakeBack>;
}) {
  const { message, turn, line } = item;
  const mine = message.author_user_id === me.user.id;
  const hasDoc = typeof message.data.doc_id === "string";
  return (
    <li data-composer-queue-item={message.id} className="px-1.5 py-1">
      <div className="flex min-h-7 items-center gap-2">
        <p title={message.body} className="min-w-0 flex-1 truncate text-[14px] text-foreground">
          {mine ? null : (
            <span className="text-muted-foreground">{authorOf(message, me).name}: </span>
          )}
          {message.body}
        </p>
        {hasDoc ? (
          <FileText aria-label="With a doc" className="size-3.5 shrink-0 text-muted-foreground" />
        ) : null}
        {line && turn ? (
          <QueueActions line={line} turn={turn} mine={mine} onEdit={onEdit} takeBack={takeBack} />
        ) : null}
      </div>
      {line ? null : <NotSent message={message as UnsentView} sends={sends} />}
    </li>
  );
}

/** A taken-back follow-up's words, doc and files (slice 5b), back in the reply box. */
type OnEdit = (taken: { body: string; doc_id: string | null; files?: Uploaded[] }) => void;

/** Edit and × on a docked follow-up while the computer hasn't taken it; "Sending…" before that. */
function QueueActions({
  line,
  turn,
  mine,
  onEdit,
  takeBack,
}: {
  line: TurnLine;
  turn: Turn;
  mine: boolean;
  onEdit: OnEdit;
  takeBack: ReturnType<typeof useTakeBack>;
}) {
  const changing = takeBack.isPending && takeBack.variables === turn.id;
  const failed = takeBack.isError && takeBack.variables === turn.id;
  if (line.kind !== "queued") {
    return (
      <span data-turn-line={line.kind} className="shrink-0 text-meta text-muted-foreground">
        {line.kind === "sending" ? line.text : null}
      </span>
    );
  }
  return (
    <div data-turn-line="queued" data-turn-id={turn.id} className="flex shrink-0 items-center">
      <span className="sr-only">{line.text}</span>
      {failed ? (
        <span className="mr-1 text-meta text-brick">{errorHint(takeBack.error)}</span>
      ) : null}
      {mine ? (
        <>
          <Button
            size="icon-xs"
            variant="ghost"
            aria-label="Edit"
            disabled={changing}
            onClick={() =>
              takeBack.mutate(turn.id, {
                onSuccess: onEdit,
              })
            }
          >
            <Pencil />
          </Button>
          <Button
            size="icon-xs"
            variant="ghost"
            aria-label="Remove"
            disabled={changing}
            onClick={() => takeBack.mutate(turn.id)}
          >
            <X />
          </Button>
        </>
      ) : null}
    </div>
  );
}

/**
 * The reply box. In the side panel, Ask to change's doc rides along (slice 2d
 * spec §10.5): its chip, "Update this doc: " typed in, focused, and `doc_id`
 * on the message; the chip goes once it's sent. Follow-ups queued behind a
 * reply sit in a tray behind its top edge.
 */
function ReplyComposer({
  busy,
  size,
  send,
  composer,
  texted,
  queue,
  me,
  sends,
  stoppable,
  threadId,
  files,
}: {
  busy: boolean;
  size: Size;
  /**
   * Slice 5b (spec §6.2): files attached here go with the reply, by id; an
   * Edited follow-up's or a Not sent reply's come back as its chips
   * (`LoadedThread` holds them, Codex on #435).
   */
  files: ReturnType<typeof useComposerFiles>;
  /** Sends a reply, and brings it into view (`LoadedThread`). */
  send: (ask: Ask) => void;
  composer: RefObject<ComposerHandle | null>;
  /**
   * The person's own linked iMessage thread (slice 2e, Paper 64) or email
   * chat (slice 2h): they can text or email it too. Null for any other.
   */
  texted: "imessage" | "email" | null;
  queue: ComposerQueueItem[];
  me: Me;
  sends: Sends;
  /** The turn being answered: an empty box's Send is Stop (slice 1i spec). */
  stoppable: Turn | null;
  threadId: string;
}) {
  const gate = useAskGate();
  const stop = useStop(threadId, stoppable);
  const replyDoc = useReplyDoc(size, composer);
  const { key, docId, clearDoc } = replyDoc;
  // An Edited follow-up's files come back as its chips, its words without
  // the name the files gave it (Devin on #435).
  const onEdit: OnEdit = (taken) => {
    const back = taken.files ?? [];
    files.put(back);
    replyDoc.onEdit({ ...taken, body: editedBody(taken.body, back) });
  };
  return (
    <div className={DENSITY[size].composer}>
      <ComposerQueue queue={queue} me={me} sends={sends} onEdit={onEdit} />
      <div className="relative">
        <Composer
          key={key}
          ref={composer}
          clearOnSubmit
          size="md"
          disabled={!gate.open}
          notice={gateNotice(gate)}
          placeholder={replyPlaceholder(busy, texted)}
          label="Reply in thread"
          initialText={docId ? ASK_TEXT : ""}
          autoFocus={Boolean(docId)}
          {...filesProps(files, docChip(docId, clearDoc), gate.open)}
          stop={stop.button}
          onSubmit={(body) => {
            // Shown at once, with its doc's chip and its files; Retry sends them again.
            sendReply(send, body, docId, files);
            if (docId) clearDoc();
          }}
        />
      </div>
      {/* A failed send says so on its message ("Not sent"); here only the gate, and a Stop that didn't take. */}
      <AskNotes gate={gate} error={stop.error} className="mt-2" />
    </div>
  );
}

/**
 * The doc a reply carries: in the side panel, Ask to change's; in any size,
 * an Edited follow-up's, back as its chip.
 */
function useReplyDoc(size: Size, composer: RefObject<ComposerHandle | null>) {
  const attach = useAttach();
  const [edited, setEdited] = useState<string | null>(null);
  const panel = size === "sm";
  const onEdit: OnEdit = (taken) => {
    setEdited(taken.doc_id);
    composer.current?.put(taken.body);
  };
  return {
    key: panel ? attach.key : undefined,
    docId: edited ?? (panel ? attach.docId : null),
    clearDoc: () => {
      setEdited(null);
      if (panel) attach.clear();
    },
    onEdit,
  };
}

/** The attached doc's chip in the composer, with its ×; none with no doc. */
const docChip = (docId: string | null, onRemove: () => void) =>
  docId ? <ComposerDocChip docId={docId} onRemove={onRemove} /> : undefined;

/** A reply with its doc and files (slice 5b: no words, the files name it), the files then cleared. */
function sendReply(
  send: (ask: Ask) => void,
  body: string,
  docId: string | null,
  files: ReturnType<typeof useComposerFiles>,
) {
  const sent = files.ready;
  send({
    body: body || wordlessBody(sent),
    ...(docId ? { doc_id: docId } : {}),
    ...(sent.length ? { files: sent } : {}),
  });
  files.clear();
}

/** The app the person's own linked thread also goes by (slice 2e, 2h), else null. */
function textedBy(detail: ThreadDetail, me: Me): "imessage" | "email" | null {
  if (!detail.messaging?.linked || detail.thread.started_by_user_id !== me.user.id) return null;
  return detail.thread.messaging_app ?? "imessage";
}

/**
 * What the empty reply box says: a reply queues while one runs; a texted
 * thread can be texted, an email chat emailed (slice 2h, Paper 77b).
 */
function replyPlaceholder(busy: boolean, texted: "imessage" | "email" | null): string {
  if (busy) return "Reply to crew";
  if (texted === "email") return "Reply here, or by email.";
  return texted ? "Reply here, or text it." : "Reply in thread.";
}

/**
 * The reply box's Stop (slice 1i spec): for the turn being answered, and
 * "Stopping" from the press until the turn ends. A Stop that didn't take is
 * shown, except for a reply that finished as it was pressed
 * (`bad_transition`): the thread shows how it ended.
 */
function useStop(threadId: string, stoppable: Turn | null) {
  const stopTurn = useStopTurn(threadId);
  const error = stopTurn.error;
  const pressed = stopTurn.isPending && stopTurn.variables === stoppable?.id;
  return {
    button: stoppable
      ? {
          onStop: () => stopTurn.mutate(stoppable.id),
          stopping: Boolean(stoppable.stop_requested_at) || pressed,
        }
      : undefined,
    error: error instanceof ApiError && error.code === "bad_transition" ? null : error,
  };
}

/** "3 replies" under the opener; "Latest messages" when older ones aren't loaded. */
function RepliesEyebrow({ banded, messages }: { banded: boolean; messages: ViewMessage[] }) {
  return <Eyebrow>{banded ? replyCountLabel(replyCount(messages)) : "Latest messages"}</Eyebrow>;
}

function LoadEarlier({
  earlier,
  before,
}: {
  earlier: ReturnType<typeof useEarlierMessages>;
  before: string;
}) {
  if (!earlier.hasMore) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="self-center"
      disabled={earlier.load.isPending}
      onClick={() => earlier.load.mutate(before)}
    >
      Load earlier messages
    </Button>
  );
}

/**
 * Paging back past the latest 200 messages (`?before=`). Only there when the
 * loaded window doesn't reach the start of the thread.
 */
function EarlierMessages({
  detail,
  turns,
  tasks,
  docs,
  me,
  size,
}: {
  detail: ThreadDetail;
  turns: Map<string, Turn>;
  tasks: Map<string, TaskItem>;
  /** The thread's docs by id, for its doc cards and chips (slice 2d spec §8). */
  docs: Map<string, DocItem>;
  me: Me;
  size: Size;
}) {
  const earlier = useEarlierMessages(detail.thread.id);
  if (!detail.has_more) return null;
  const { rows, before } = olderThan(earlier.messages, detail.messages);
  return (
    <>
      <LoadEarlier earlier={earlier} before={before} />
      {rows.map((row, i) => (
        <RowView
          key={row.key}
          row={row}
          continued={continuesRow(rows[i - 1], row)}
          turns={turns}
          tasks={tasks}
          docs={docs}
          me={me}
          size={size}
          threadId={detail.thread.id}
        />
      ))}
    </>
  );
}

/** An Edited start's words and doc, for the surface to start the thread again (`starts.ts`). */
/**
 * Edit on a Not sent message: the start goes back to where threads start
 * (the thread never existed), and null; a reply's words and files, for the
 * reply composer (Codex on #435).
 */
function takeUnsent(
  threadId: string,
  id: string,
  post: ReturnType<typeof usePostMessage>,
  onStartEdited: StartEdited,
) {
  const start = outbox.find(threadId, id)?.start ? post.editStart() : null;
  if (!start) return post.edit(id);
  onStartEdited(start);
  return null;
}

export type StartEdited = (start: {
  body: string;
  doc_id: string | null;
  files: Uploaded[];
}) => void;

/** A phone's filed chat has its Area's tab hanging below the header (Paper HF): room for it. */
const tabRoom = (detail: ThreadDetail) =>
  detail.thread.area && detail.thread.messaging_app !== "email" ? "max-md:pt-9" : null;

/** The thread's tasks and docs by id, for its cards. */
function threadMaps(detail: ThreadDetail) {
  return {
    tasks: new Map((detail.tasks ?? []).map((t) => [t.id, t])),
    docs: new Map((detail.docs ?? []).map((d) => [d.id, d])),
  };
}

// fallow-ignore-next-line complexity -- over 15 before S-509, which only moved its row list out
function LoadedThread({
  detail,
  projection,
  me,
  size,
  onStartEdited,
}: {
  detail: ThreadDetail;
  projection: Projection;
  me: Me;
  size: Size;
  onStartEdited: StartEdited;
}) {
  const d = DENSITY[size];
  const threadId = detail.thread.id;
  const unsent = useUnsent(threadId);
  // The replies this tab is sending or couldn't send, merged after the thread's own.
  const merged = unsentLayout(detail, projectedMessages(projection), unsent);
  const messages = merged.messages;
  // Slice 6c §5: someone holds the computer, so the next ask waits for them.
  const holder = useHolder(me);
  const { opener, rows, busy, turns, queue, stoppable } = threadLayout(
    merged.detail,
    messages,
    me.computer,
    holder,
  );
  // Once the thread holds a reply, the outbox lets it go.
  useEffect(() => {
    const loaded = new Set(detail.messages.map((m) => m.id));
    outbox.settle(threadId, loaded);
  }, [threadId, detail.messages]);
  const post = usePostMessage(threadId, me.user.id);
  const composer = useRef<ComposerHandle>(null);
  const files = useComposerFiles();
  const stick = useRef<StickToBottomContext>(null);
  const resize = useFollowResize();
  // The person's own send always shows, however far up they'd read: back to the
  // bottom and pinned there again, so the reply after it streams in view too.
  // Anyone else's message, and the reply's blocks, follow only a pinned view.
  const showSent = () => void stick.current?.scrollToBottom();
  const sends: Sends = {
    retry: (id) => {
      post.retry(id);
      showSent();
    },
    edit: (id) => {
      const taken = takeUnsent(threadId, id, post, onStartEdited);
      if (!taken) return;
      files.put(taken.files);
      composer.current?.put(editedBody(taken.body, taken.files));
    },
  };
  const { tasks, docs } = threadMaps(detail);
  const handoffs = handoffsById(detail);
  const approvals = new Map((detail.approvals ?? []).map((a) => [a.id, a]));
  const routines = detail.routines ? new Map(detail.routines.map((r) => [r.id, r])) : null;
  // From `md` up the opener is pinned above the replies, clamped; on a phone the
  // conversation has the room, so it's the first row, whole, and scrolls away.
  const pinned = useWide();
  return (
    <Watching turns={turns} me={me}>
      <ThreadHandoffs.Provider value={handoffs}>
        <ThreadApprovals.Provider value={approvals}>
          <ThreadLive.Provider value={projection}>
            <ThreadRoutines.Provider value={routines}>
              <div data-thread-view={threadId} className="flex min-h-0 flex-1 flex-col">
                {opener && pinned ? (
                  <div className={d.band}>
                    <MessageView
                      message={opener}
                      turn={undefined}
                      me={me}
                      size={size}
                      tasks={tasks}
                      docs={docs}
                      clamp={d.clamp}
                      highlight={d.highlight}
                      sends={sends}
                    />
                  </div>
                ) : null}
                <StickToBottom
                  contextRef={stick}
                  className="relative min-h-0 flex-1"
                  resize={resize}
                  initial="instant"
                >
                  <StickToBottom.Content
                    scrollClassName="overflow-y-auto overscroll-y-contain"
                    className={cn("flex flex-col", d.rows, size === "md" && tabRoom(detail))}
                  >
                    <EarlierMessages
                      detail={detail}
                      turns={turns}
                      tasks={tasks}
                      docs={docs}
                      me={me}
                      size={size}
                    />
                    {opener && !pinned ? (
                      <MessageView
                        message={opener}
                        turn={undefined}
                        me={me}
                        size={size}
                        tasks={tasks}
                        docs={docs}
                        sends={sends}
                      />
                    ) : null}
                    <RepliesEyebrow banded={Boolean(opener)} messages={messages} />
                    <ThreadRowList
                      rows={rows}
                      turns={turns}
                      tasks={tasks}
                      docs={docs}
                      me={me}
                      size={size}
                      threadId={threadId}
                      sends={sends}
                    />
                  </StickToBottom.Content>
                  <ScrollToBottom />
                </StickToBottom>
                <ReplyComposer
                  busy={busy}
                  size={size}
                  send={(ask) => {
                    post.send(ask);
                    showSent();
                  }}
                  composer={composer}
                  queue={queue}
                  me={me}
                  sends={sends}
                  stoppable={stoppable}
                  threadId={threadId}
                  texted={textedBy(detail, me)}
                  files={files}
                />
              </div>
            </ThreadRoutines.Provider>
          </ThreadLive.Provider>
        </ThreadApprovals.Provider>
      </ThreadHandoffs.Provider>
    </Watching>
  );
}

function ThreadPending({ error, size }: { error: Error | null; size: Size }) {
  if (!error) {
    return (
      <div className="flex flex-1 items-center justify-center text-muted-foreground">
        <Spinner className="size-5" />
      </div>
    );
  }
  const gone = error instanceof ApiError && error.status === 404;
  return (
    <p className={cn("text-body text-muted-foreground", DENSITY[size].note)}>
      {gone ? "This thread isn't in your workspace." : errorHint(error)}
    </p>
  );
}

export function ThreadView({
  threadId,
  me,
  size = "md",
  onStartEdited,
}: {
  threadId: string;
  me: Me;
  size?: Size;
  /** Edit on a start that wasn't sent: the surface takes its words back. */
  onStartEdited: StartEdited;
}) {
  const { query, projection } = useThreadProjection(threadId);
  if (!projection.loaded) return <ThreadPending error={query.error} size={size} />;
  return (
    <LoadedThread
      detail={projection.loaded}
      projection={projection}
      me={me}
      size={size}
      onStartEdited={onStartEdited}
    />
  );
}
